import React, { createContext, useContext, useEffect, useState } from "react";

const dict = {
  en: {
    nav: {
      home: "Home",
      services: "Services",
      pricing: "Pricing",
      faq: "FAQ",
      book: "Book now",
      book_short: "Book",
      portal_short: "Booking",
      install_short: "Install",
      installed_short: "Installed",
      admin: "Admin",
      portal: "Manage booking",
    },
    install: {
      title: "Install LocalH2O",
      subtitle: "Get one-tap access from your home screen.",
      installed_title: "You're all set!",
      installed_subtitle: "LocalH2O is already installed on this device.",
      installed_short_label: "App installed",
      installed_toast: "LocalH2O installed — open it from your home screen!",
      no_prompt_available:
        "Direct install isn't available in this browser — follow the on-screen steps to add LocalH2O.",
      installed_body:
        "Look for the LocalH2O icon on your home screen — booking is one tap away.",
      detected_ios: "iOS · Safari",
      detected_android: "Android",
      detected_desktop: "Desktop",
      desktop_manual:
        "In Chrome / Edge / Brave: click the install icon in the address bar (or open the browser menu and choose ‘Install LocalH2O’).",
      footer_note:
        "Free, no account needed. The app works offline for browsing past bookings.",
    },
    pwa: {
      title: "Add LocalH2O to your home screen",
      subtitle: "One-tap access — book in seconds.",
      ios_step1: "Tap the Share icon in Safari",
      ios_step2: "Choose ‘Add to Home Screen’",
      ios_step3: "Tap ‘Add’ — done!",
      install_btn: "Install app",
      android_manual:
        "Open your browser menu and choose ‘Install app’ or ‘Add to Home Screen’.",
      fallback:
        "Bookmark localh2o.com on your phone for quick access on your next clean.",
    },
    chat_fab: {
      open_chat: "Open chat",
      title: "Chat with LocalH2O",
      subtitle: "We typically reply in a few minutes.",
      intro:
        "Hi! 👋 Got a question before booking? Tell us your name and the best way to reach you, and we'll reply right here.",
      active_intro:
        "You're chatting live with the LocalH2O team. We'll keep this thread for 72 hours.",
      name_label: "Your name",
      name_placeholder: "e.g. Anna Kowalska",
      contact_label: "Email or phone",
      contact_placeholder: "you@example.com or +48 …",
      start_btn: "Start chat",
      disclaimer:
        "We only use these details to reply to you. No marketing, no spam.",
      empty: "No messages yet — say hi to start the conversation!",
      error_name: "Please enter your name.",
      error_contact: "Enter a valid email or phone number.",
      error_generic: "We couldn't start the chat. Please try again.",
      error_send: "Couldn't send message. Try again.",
    },
    hero: {
      eyebrow: "Local · Gdańsk",
      title_a: "Crystal-clean",
      title_b: "driveways & bins.",
      subtitle:
        "Pressure washing for driveways, paths and patios. Hygienic, on-the-spot wheelie bin cleaning. We connect to your outdoor tap — you supply the water, we bring everything else. Locally run, neighbour-trusted.",
      cta: "Book a service",
      cta_secondary: "See pricing",
      stat_jobs: "jobs completed",
      stat_rating: "average rating",
      stat_response: "h response time",
    },
    hero_badge: {
      title: "Trusted local team",
      sub: "Gdańsk · Sopot · Gdynia",
    },
    trust: {
      eyebrow: "Gdańsk · Pomerania",
      title: "Locally run. Locally trusted.",
      body: "We're a small Gdańsk-based crew with a big love for the Baltic coast and clean driveways. From Stogi to Oliwa, we've got you covered.",
    },
    final_cta: {
      title: "Ready for a sparkling clean driveway?",
      subtitle: "Book online in 60 seconds. Pay after the job is done.",
    },
    footer: {
      driveway: "Pressure Washing",
      bin: "Wheelie Bin Cleaning",
      tagline:
        "Pressure washing & wheelie bin cleaning, run by locals — for locals. Gdańsk & surrounding areas.",
    },
    reviews_section: {
      eyebrow: "What locals say",
      title: "Sparkling reviews from neighbours",
      subtitle:
        "Real bookings, real driveways, real bins — straight from Gdańsk and around.",
    },
    reviews: [
      {
        text: "Driveway looks brand new — couldn't be happier!",
        author: "Marta K.",
        location: "Oliwa",
        rating: 5,
      },
      {
        text: "On time, professional, and the bins came back smelling fresh. Best 35 PLN I've ever spent.",
        author: "Paweł W.",
        location: "Wrzeszcz",
        rating: 5,
      },
      {
        text: "Booked online in two minutes. They removed years of moss from my path — incredible result.",
        author: "Anna L.",
        location: "Sopot",
        rating: 5,
      },
      {
        text: "Quick, polite, and very thorough. We're already booking again next month.",
        author: "Tomasz B.",
        location: "Przymorze",
        rating: 5,
      },
      {
        text: "I was skeptical at first but they turned my filthy paving back into a feature. Highly recommend!",
        author: "Karolina S.",
        location: "Gdynia",
        rating: 5,
      },
    ],
    services: {
      title: "Two services. Done properly.",
      subtitle:
        "We focus on what we do best — and we do it with the right kit, the right detergents and the right pressure for the surface.",
      learn: "Learn more",
      from: "from",
    },
    service_detail: {
      included: "What's included",
      payment_note:
        "Cash on completion or bank transfer — pay only when you're happy with the result.",
      also_available_eyebrow: "Also available",
      also_available_title: "Pair it with our other service",
      also_available_subtitle:
        "Most LocalH2O customers book both — get crystal-clean paving and fresh bins on the same visit.",
    },
    how: {
      title: "How it works",
      subtitle: "From booking to sparkling clean, in three steps.",
      s1_t: "Pick a service & slot",
      s1_d: "Choose between driveway cleaning or bin cleaning, then pick a date and time that suits you.",
      s2_t: "We arrive equipped",
      s2_d: "We bring the kit, power and eco-friendly detergents — we just connect to your outdoor cold-water tap. Hot water available if you have an outside hot tap.",
      s3_t: "Sparkling result",
      s3_d: "We clean, rinse and disinfect on the spot. Pay by cash or bank transfer once you're happy.",
    },
    pricing: {
      title: "Honest, transparent pricing",
      subtitle: "Final price confirmed once we know your area. No hidden fees, ever.",
      currency: "PLN",
      eco: "Eco-friendly",
      local: "Locally owned",
    },
    faq: {
      title: "Frequently asked questions",
      items: [
        {
          q: "Do I need to be home during the service?",
          a: "Not necessarily for driveways — as long as we have outdoor access. For bin cleaning we just need the bins out, we don't need entry to the property.",
        },
        {
          q: "Are the detergents safe for pets and plants?",
          a: "Yes. We use biodegradable, eco-friendly detergents that are safe around pets and gardens once rinsed.",
        },
        {
          q: "How do I pay?",
          a: "Cash on completion or bank transfer. We send a receipt by email after the job is done.",
        },
        {
          q: "What areas do you cover?",
          a: "Gdańsk and surrounding areas (Sopot, Gdynia, Pruszcz Gdański). For locations further out, please contact us.",
        },
      ],
    },
    contact: {
      title: "Get in touch",
      subtitle: "Questions before booking? Drop us a line.",
      email: "hello@localh2o.com",
      phone: "+48 881 476 730",
      address: "Gdańsk, Poland",
    },
    booking: {
      title: "Book a service",
      step_service: "Service",
      step_schedule: "Schedule",
      step_details: "Details",
      step_review: "Review",
      pick_services: "Pick the services you need",
      qty: "Qty",
      area_label: "Enter the area to clean",
      area_placeholder: "e.g. 45",
      area_unit: "m²",
      area_subtotal: "Subtotal",
      area_min_hint: "Minimum 1 m². Final price confirmed once we see the area.",
      bin_label: "How many bins?",
      bin_unit_singular: "bin",
      bin_unit_plural: "bins",
      pick_date: "Pick a date",
      pick_time: "Pick a time slot",
      no_slots: "No slots available — try another date.",
      blocked: "This date is unavailable",
      contact_info: "Contact information",
      address: "Service address",
      name: "Full name",
      email: "Email",
      phone: "Phone",
      street: "Street & number",
      city: "City",
      postal: "Postal code",
      postal_outside_area:
        "We don't currently cover this postal code — leave a note and we'll be in touch.",
      postal_invalid: "Use the format NN-NNN (e.g. 80-001)",
      notes: "Notes (optional)",
      payment: "Payment method",
      cash: "Cash on completion",
      bank: "Bank transfer",
      water_section: "Water supply",
      water_intro:
        "We connect to your outdoor water tap — please confirm what's available at the property.",
      has_cold: "I have an accessible outdoor cold-water tap",
      has_hot: "I have an outdoor hot-water tap (optional — enables hot-water cleaning)",
      no_tap_warning:
        "An outdoor cold-water tap is required for the service. Please tick the box only if you have one.",
      back: "Back",
      next: "Next",
      review_title: "Review & confirm",
      total: "Total",
      confirm: "Confirm booking",
      success_title: "Booking confirmed!",
      success_text: "We've received your booking. Our team will call you to confirm the details.",
      booking_id: "Booking reference",
      back_home: "Back to home",
    },
    admin: {
      login: "Admin login",
      email: "Email",
      password: "Password",
      sign_in: "Sign in",
      logout: "Sign out",
      dashboard: "Dashboard",
      bookings: "Bookings",
      services_tab: "Services",
      customers: "Customers",
      messages: "Messages",
      messages_empty: "No conversations yet.",
      messages_select: "Select a conversation",
      messages_select_hint:
        "Pick a customer on the left to view chat history and reply.",
      messages_typing_placeholder: "Reply to customer…",
      messages_send: "Send",
      messages_open_booking: "Open booking",
      messages_unread: "unread",
      messages_search_placeholder: "Search by name, reference, or phone…",
      messages_visitor_badge: "Visitor",
      messages_archived_badge: "Archived",
      messages_archive: "Archive chat",
      messages_unarchive: "Unarchive",
      messages_show_archived: "Show archived",
      messages_delete: "Delete chat",
      messages_delete_confirm_title: "Delete this conversation?",
      messages_delete_confirm_body:
        "All messages in this chat will be permanently removed. This cannot be undone.",
      messages_delete_confirm: "Delete forever",
      messages_archived_toast: "Chat archived",
      messages_unarchived_toast: "Chat restored",
      messages_deleted_toast: "Chat deleted",
      contact_title: "Public contact details",
      contact_subtitle:
        "Shown in the website footer, JSON-LD for SEO, and on every customer email. Updates apply instantly across the site.",
      contact_email_label: "Public email address",
      contact_phone_label: "Public phone number",
      contact_address_pl_label: "Address (Polish)",
      contact_address_en_label: "Address (English)",
      contact_save: "Save changes",
      contact_discard: "Discard",
      contact_last_saved: "Last saved",
      contact_saved: "Contact details updated",
      contact_save_failed: "Couldn't save — please try again.",
      contact_reviews_baseline_label: "Reviews counter baseline",
      contact_reviews_baseline_help:
        "The number shown next to the hero stat. Actual reviews you generate or add are added on top — set this to 0 if you want the public counter to match exactly the number of real reviews.",
      reviews_title: "Customer reviews",
      reviews_subtitle:
        "Generate AI-written, bilingual (PL + EN) reviews in one click, or delete any that don't fit. They appear on the homepage carousel and feed the public reviews counter.",
      reviews_total_label: "Public counter shows",
      reviews_generate_count_label: "How many to generate",
      reviews_generate_btn: "Generate",
      reviews_generate_help:
        "Uses Gemini Flash. Each generation produces ~{n} unique reviews with realistic Polish names and Gdańsk neighbourhoods. Max 20 per click.",
      reviews_generated_toast: "{n} new reviews added",
      reviews_generate_failed: "Couldn't generate — please try again.",
      reviews_deleted_toast: "Review deleted",
      reviews_delete_failed: "Couldn't delete — please try again.",
      reviews_delete: "Delete review",
      reviews_list_label: "Existing reviews",
      reviews_empty: "No reviews yet — click Generate to add some.",
      services_add: "Add service",
      services_edit: "Edit",
      services_save: "Save",
      services_cancel: "Cancel",
      services_delete: "Delete",
      services_active: "Active",
      services_inactive: "Inactive",
      services_slug: "Slug (URL)",
      services_name_en: "Name (EN)",
      services_name_pl: "Name (PL)",
      services_unit_en: "Unit (EN)",
      services_unit_pl: "Unit (PL)",
      services_price: "Base price (PLN)",
      services_duration: "Duration (min)",
      services_desc_en: "Description (EN)",
      services_desc_pl: "Description (PL)",
      services_features_en: "Features (EN, one per line)",
      services_features_pl: "Features (PL, jeden na linię)",
      services_saved: "Service saved",
      services_deleted: "Service deleted",
      services_created: "Service created",
      services_delete_confirm_title: "Delete this service?",
      services_delete_confirm_body:
        "It will be removed permanently. Existing bookings keep their service info.",
      settings: "Settings",
      kpi_today: "Bookings today",
      kpi_week: "Bookings this week",
      kpi_revenue: "Revenue (week)",
      kpi_completion: "Completion rate",
      filter_status: "Status",
      filter_service: "Service",
      filter_from: "From",
      filter_to: "To",
      all: "All",
      timeline: "Status timeline",
      update_status: "Update status",
      blackouts: "Blackout dates",
      add_blackout: "Add blackout",
      reason: "Reason",
      add: "Add",
      remove: "Remove",
      archive: "Archive",
      unarchive: "Unarchive",
      show_archived: "Show archived",
      archived_label: "Archived",
      delete: "Move to trash",
      delete_confirm_title: "Move this booking to trash?",
      delete_confirm_body:
        "This booking will be moved to the Trash tab. You can restore it later, or permanently delete it from there.",
      delete_cancel: "Cancel",
      delete_confirm: "Move to trash",
      deleted_toast: "Moved to trash",
      trash: "Trash",
      restore: "Restore",
      restored_toast: "Restored",
      purge: "Delete forever",
      purge_confirm_title: "Permanently delete this booking?",
      purge_confirm_body:
        "This will permanently remove the booking. This action cannot be undone.",
      purge_confirm: "Delete forever",
      purged_toast: "Permanently deleted",
      empty_trash: "Empty trash",
      empty_trash_confirm_title: "Empty the trash?",
      empty_trash_confirm_body:
        "All trashed bookings will be permanently removed. This cannot be undone.",
      trash_empty: "Trash is empty",
    },
    portal: {
      nav: "Manage booking",
      title: "Manage your booking",
      subtitle:
        "Look up your booking with your reference and email or phone — then edit details, reschedule, or chat with us in real time.",
      lookup_reference: "Booking reference",
      lookup_reference_placeholder: "e.g. GDA-1234-A9F",
      lookup_contact: "Email or phone",
      lookup_contact_placeholder: "you@example.com or +48 …",
      lookup_btn: "Open my booking",
      lookup_help:
        "Your reference appears on the confirmation screen and any email we sent you.",
      lookup_error_not_found: "We couldn't find a booking with those details.",
      lookup_error_mismatch: "Email or phone doesn't match this booking.",
      lookup_error_generic: "Something went wrong. Please try again.",
      errors: {
        generic:
          "Something didn't work — please try again. If it keeps failing, get in touch and we'll sort it.",
        offline:
          "You appear to be offline. Check your internet connection and try again.",
        network:
          "Couldn't reach our servers. Check your connection and try again — your changes are still here.",
        timeout:
          "The request took too long. Please try again in a moment.",
        bad_request:
          "Some of the details look invalid. Please double-check the form and try again.",
        session_expired:
          "Your session has expired. Please look up your booking again.",
        forbidden:
          "You don't have permission to do that on this booking.",
        not_found:
          "We couldn't find that booking — it may have been cancelled or removed.",
        lookup_not_found:
          "No booking matches that reference and contact. Please check both and try again.",
        lookup_mismatch:
          "Reference is correct but the email or phone doesn't match it.",
        conflict:
          "That action conflicts with the current booking state. Please refresh and try again.",
        slot_taken:
          "That time slot has just been taken. Please pick a different time.",
        already_cancelled:
          "This booking has already been cancelled.",
        gone:
          "This booking is no longer available — it may have been completed or archived.",
        validation:
          "Some fields aren't filled in correctly. Please review the form and try again.",
        rate_limited:
          "Too many requests in a short time. Please wait a minute and try again.",
        server:
          "Our servers are having a moment. Please try again in a minute.",
      },
      details_tab: "Booking details",
      chat_tab: "Live chat",
      reference: "Reference",
      status: "Status",
      scheduled: "Scheduled for",
      reschedule: "Reschedule",
      reschedule_title: "Pick a new date & time",
      reschedule_save: "Confirm new slot",
      cancel: "Cancel booking",
      cancel_confirm_title: "Cancel this booking?",
      cancel_confirm_body:
        "We'll mark your booking as cancelled. You can always book again from the home page.",
      cancel_back: "Keep booking",
      cancel_yes: "Yes, cancel",
      cancelled_toast: "Booking cancelled",
      saved_toast: "Booking updated",
      rescheduled_toast: "Booking rescheduled",
      readonly_notice:
        "This booking is in {status} — details are now read-only. Need a change? Use the live chat.",
      service_label: "Service & quantity",
      contact_section: "Your details",
      address_section: "Service address",
      taps_section: "Water supply",
      save: "Save changes",
      logout: "Sign out of portal",
      empty_chat: "No messages yet — say hi!",
      chat_placeholder: "Type your message…",
      chat_send: "Send",
      chat_admin_label: "LocalH2O team",
      chat_you: "You",
      chat_offline_warning: "Connection lost — reconnecting…",
      chat_intro:
        "Chat directly with the LocalH2O team. Replies arrive in real time.",
    },
    errors: {
      required: "This field is required",
      email_invalid: "Please enter a valid email address",
      phone_short: "Please enter a valid phone number",
      cold_tap_required: "Outdoor cold-water tap is required to book",
      slot_taken: "Sorry, that time slot was just taken — please pick another.",
      date_unavailable: "That date is unavailable. Please choose another.",
      generic: "Couldn't create your booking. Please review your details.",
    },
    status: {
      pending: "Pending",
      confirmed: "Confirmed",
      in_progress: "In progress",
      completed: "Completed",
      cancelled: "Cancelled",
    },
    common: {
      currency: "PLN",
      lang: "EN",
      switch_lang: "Switch language",
    },
    seo: {
      home_title:
        "LocalH2O — Pressure Washing & Wheelie Bin Cleaning · Gdańsk",
      home_desc:
        "Eco-friendly driveway pressure washing and wheelie bin cleaning across Gdańsk and the Pomeranian Voivodeship. Book online in 60 seconds, pay after the job is done.",
      service_driveway_title:
        "Pressure Washing in Gdańsk — driveways, patios & paths · LocalH2O",
      service_driveway_desc:
        "Restore your driveway, patio and paths with LocalH2O's high-pressure cleaning. From 10 PLN/m². Book online for Gdańsk, Sopot, Gdynia and across Pomorskie.",
      service_bin_title:
        "Wheelie Bin Cleaning & Disinfection · Gdańsk · LocalH2O",
      service_bin_desc:
        "Hygienic, on-the-spot wheelie bin cleaning with food-safe disinfectants — 35 PLN per bin. Serving Gdańsk, Sopot, Gdynia and Pomorskie Voivodeship.",
      booking_title: "Book a service · LocalH2O",
      booking_desc:
        "Reserve a driveway pressure-washing or wheelie-bin-cleaning slot in 60 seconds. Pay after the job. LocalH2O · Gdańsk.",
      confirm_title: "Booking confirmed · LocalH2O",
      confirm_desc: "Your LocalH2O booking has been received.",
      admin_title: "Admin · LocalH2O",
      admin_desc: "LocalH2O administration panel.",
      portal_title: "Manage your booking · LocalH2O",
      portal_desc:
        "View, edit, reschedule or cancel your LocalH2O booking — and chat with us live from your customer portal.",
    },
  },
  pl: {
    nav: {
      home: "Strona główna",
      services: "Usługi",
      pricing: "Cennik",
      faq: "FAQ",
      book: "Zarezerwuj",
      book_short: "Rezerwuj",
      portal_short: "Moja",
      install_short: "Zainstaluj",
      installed_short: "Zainstalowane",
      admin: "Panel",
      portal: "Moja rezerwacja",
    },
    install: {
      title: "Zainstaluj LocalH2O",
      subtitle: "Dostęp jednym dotknięciem prosto z ekranu głównego.",
      installed_title: "Wszystko gotowe!",
      installed_subtitle: "LocalH2O jest już zainstalowane na tym urządzeniu.",
      installed_short_label: "Aplikacja zainstalowana",
      installed_toast:
        "LocalH2O zainstalowane — otwórz z ekranu głównego!",
      no_prompt_available:
        "Bezpośrednia instalacja nie jest dostępna w tej przeglądarce — postępuj zgodnie z wyświetlonymi krokami.",
      installed_body:
        "Poszukaj ikony LocalH2O na swoim ekranie głównym — rezerwacja jest na wyciągnięcie ręki.",
      detected_ios: "iOS · Safari",
      detected_android: "Android",
      detected_desktop: "Komputer",
      desktop_manual:
        "W Chrome / Edge / Brave: kliknij ikonę instalacji w pasku adresu (lub otwórz menu przeglądarki i wybierz „Zainstaluj LocalH2O”).",
      footer_note:
        "Bezpłatnie, bez konta. Aplikacja działa offline do przeglądania wcześniejszych rezerwacji.",
    },
    pwa: {
      title: "Dodaj LocalH2O do ekranu głównego",
      subtitle: "Dostęp jednym dotknięciem — rezerwuj w kilka sekund.",
      ios_step1: "Stuknij ikonę Udostępnij w Safari",
      ios_step2: "Wybierz „Do ekranu początkowego”",
      ios_step3: "Stuknij „Dodaj” — gotowe!",
      install_btn: "Zainstaluj aplikację",
      android_manual:
        "Otwórz menu przeglądarki i wybierz „Zainstaluj aplikację” lub „Dodaj do ekranu głównego”.",
      fallback:
        "Dodaj localh2o.com do zakładek na telefonie, aby szybko zarezerwować kolejną wizytę.",
    },
    chat_fab: {
      open_chat: "Otwórz czat",
      title: "Napisz do LocalH2O",
      subtitle: "Zwykle odpowiadamy w kilka minut.",
      intro:
        "Cześć! 👋 Masz pytanie przed rezerwacją? Zostaw imię i e-mail lub telefon — odpiszemy tutaj.",
      active_intro:
        "Rozmawiasz na żywo z zespołem LocalH2O. Wątek jest aktywny przez 72 godziny.",
      name_label: "Twoje imię",
      name_placeholder: "np. Anna Kowalska",
      contact_label: "E-mail lub telefon",
      contact_placeholder: "ty@example.com lub +48 …",
      start_btn: "Rozpocznij czat",
      disclaimer:
        "Te dane służą tylko do odpowiedzi na Twoją wiadomość. Bez marketingu, bez spamu.",
      empty: "Brak wiadomości — przywitaj się, aby rozpocząć rozmowę!",
      error_name: "Podaj swoje imię.",
      error_contact: "Podaj poprawny e-mail lub numer telefonu.",
      error_generic: "Nie udało się rozpocząć czatu. Spróbuj ponownie.",
      error_send: "Nie udało się wysłać wiadomości. Spróbuj ponownie.",
    },
    hero: {
      eyebrow: "Lokalnie · Gdańsk",
      title_a: "Krystalicznie czyste",
      title_b: "podjazdy i kubły.",
      subtitle:
        "Mycie ciśnieniowe podjazdów, ścieżek i tarasów. Higieniczne mycie kubłów na miejscu. Podłączamy się do Twojego ogrodowego kranu — Ty udostępniasz wodę, my przywozimy resztę. Lokalna firma, zaufana przez sąsiadów.",
      cta: "Zarezerwuj usługę",
      cta_secondary: "Zobacz cennik",
      stat_jobs: "wykonanych zleceń",
      stat_rating: "średnia ocena",
      stat_response: "godz. czas odpowiedzi",
    },
    hero_badge: {
      title: "Zaufany lokalny zespół",
      sub: "Gdańsk · Sopot · Gdynia",
    },
    trust: {
      eyebrow: "Gdańsk · Pomorze",
      title: "Lokalna firma. Zaufana lokalnie.",
      body: "Jesteśmy małą gdańską ekipą z wielką miłością do bałtyckiego wybrzeża i czystych podjazdów. Od Stogów po Oliwę — dojeżdżamy do Ciebie.",
    },
    final_cta: {
      title: "Gotowy na lśniąco czysty podjazd?",
      subtitle: "Zarezerwuj online w 60 sekund. Płatność po wykonaniu usługi.",
    },
    footer: {
      driveway: "Mycie ciśnieniowe",
      bin: "Mycie i dezynfekcja kubłów",
      tagline:
        "Mycie ciśnieniowe i czyszczenie kubłów na śmieci — lokalna firma dla lokalnych klientów. Gdańsk i okolice.",
    },
    reviews_section: {
      eyebrow: "Co mówią sąsiedzi",
      title: "Błyszczące opinie od mieszkańców",
      subtitle:
        "Prawdziwe rezerwacje, prawdziwe podjazdy, prawdziwe kubły — prosto z Gdańska i okolic.",
    },
    reviews: [
      {
        text: "Podjazd wygląda jak nowy — nie mogłabym być szczęśliwsza!",
        author: "Marta K.",
        location: "Oliwa",
        rating: 5,
      },
      {
        text: "Punktualnie, profesjonalnie, a kubły wróciły pachnące świeżością. Najlepsze 35 zł, jakie wydałem.",
        author: "Paweł W.",
        location: "Wrzeszcz",
        rating: 5,
      },
      {
        text: "Rezerwacja online w dwie minuty. Usunęli wieloletni mech ze ścieżki — niesamowity efekt.",
        author: "Anna L.",
        location: "Sopot",
        rating: 5,
      },
      {
        text: "Szybko, kulturalnie i bardzo dokładnie. Już rezerwujemy kolejną wizytę za miesiąc.",
        author: "Tomasz B.",
        location: "Przymorze",
        rating: 5,
      },
      {
        text: "Byłam sceptyczna, ale zamienili moją zarośniętą kostkę w prawdziwą ozdobę posesji. Polecam!",
        author: "Karolina S.",
        location: "Gdynia",
        rating: 5,
      },
    ],
    services: {
      title: "Dwie usługi. Robione porządnie.",
      subtitle:
        "Skupiamy się na tym, co umiemy najlepiej — z odpowiednim sprzętem, detergentami i ciśnieniem dopasowanym do powierzchni.",
      learn: "Dowiedz się więcej",
      from: "od",
    },
    service_detail: {
      included: "Co obejmuje usługa",
      payment_note:
        "Gotówka po wykonaniu lub przelew — płacisz dopiero, gdy efekt Cię zadowala.",
      also_available_eyebrow: "Dostępne także",
      also_available_title: "Połącz z naszą drugą usługą",
      also_available_subtitle:
        "Większość klientów LocalH2O rezerwuje obie — krystalicznie czysta kostka i świeże kubły w trakcie jednej wizyty.",
    },
    how: {
      title: "Jak to działa",
      subtitle: "Od rezerwacji do błysku w trzech krokach.",
      s1_t: "Wybierz usługę i termin",
      s1_d: "Wybierz mycie podjazdu lub kubłów, a następnie dogodną datę i godzinę.",
      s2_t: "Przyjeżdżamy z pełnym sprzętem",
      s2_d: "Przywozimy sprzęt, prąd i ekologiczne detergenty — podłączamy się do Twojego ogrodowego kranu z zimną wodą. Opcja gorącej wody, jeśli masz zewnętrzny kran z ciepłą wodą.",
      s3_t: "Lśniący efekt",
      s3_d: "Myjemy, spłukujemy i dezynfekujemy na miejscu. Płatność gotówką lub przelewem po wykonaniu.",
    },
    pricing: {
      title: "Uczciwy, przejrzysty cennik",
      subtitle: "Ostateczna cena potwierdzana po podaniu metrażu. Bez ukrytych opłat.",
      currency: "zł",
      eco: "Ekologicznie",
      local: "Lokalna firma",
    },
    faq: {
      title: "Najczęstsze pytania",
      items: [
        {
          q: "Czy muszę być w domu podczas usługi?",
          a: "Niekoniecznie — przy myciu podjazdu wystarczy dostęp do posesji. Przy kubłach wystawiamy je sami, nie wchodzimy do domu.",
        },
        {
          q: "Czy detergenty są bezpieczne dla zwierząt i roślin?",
          a: "Tak. Używamy biodegradowalnych, ekologicznych środków bezpiecznych dla zwierząt i ogrodu po spłukaniu.",
        },
        {
          q: "Jak mogę zapłacić?",
          a: "Gotówką po wykonaniu lub przelewem. Po zleceniu wysyłamy potwierdzenie e-mailem.",
        },
        {
          q: "Jaki obszar obsługujecie?",
          a: "Gdańsk i okolice (Sopot, Gdynia, Pruszcz Gdański). W przypadku dalszych lokalizacji prosimy o kontakt.",
        },
      ],
    },
    contact: {
      title: "Skontaktuj się",
      subtitle: "Masz pytania przed rezerwacją? Napisz do nas.",
      email: "hello@localh2o.com",
      phone: "+48 881 476 730",
      address: "Gdańsk, Polska",
    },
    booking: {
      title: "Zarezerwuj usługę",
      step_service: "Usługa",
      step_schedule: "Termin",
      step_details: "Dane",
      step_review: "Podsumowanie",
      pick_services: "Wybierz potrzebne usługi",
      qty: "Ilość",
      area_label: "Podaj powierzchnię do umycia",
      area_placeholder: "np. 45",
      area_unit: "m²",
      area_subtotal: "Wstępna kwota",
      area_min_hint: "Minimum 1 m². Ostateczna cena potwierdzana po obejrzeniu terenu.",
      bin_label: "Ile kubłów?",
      bin_unit_singular: "kubeł",
      bin_unit_plural: "kubły",
      pick_date: "Wybierz datę",
      pick_time: "Wybierz godzinę",
      no_slots: "Brak wolnych terminów — wybierz inny dzień.",
      blocked: "Ten dzień jest niedostępny",
      contact_info: "Dane kontaktowe",
      address: "Adres usługi",
      name: "Imię i nazwisko",
      email: "E-mail",
      phone: "Telefon",
      street: "Ulica i numer",
      city: "Miasto",
      postal: "Kod pocztowy",
      postal_outside_area:
        "Ten kod pocztowy jest poza naszym standardowym obszarem — zostaw uwagę, a oddzwonimy.",
      postal_invalid: "Użyj formatu NN-NNN (np. 80-001)",
      notes: "Uwagi (opcjonalnie)",
      payment: "Sposób płatności",
      cash: "Gotówka po wykonaniu",
      bank: "Przelew bankowy",
      water_section: "Dostęp do wody",
      water_intro:
        "Podłączamy się do Twojego ogrodowego kranu — potwierdź, co jest dostępne na posesji.",
      has_cold: "Mam dostępny zewnętrzny kran z zimną wodą",
      has_hot: "Mam zewnętrzny kran z ciepłą wodą (opcjonalnie — umożliwia mycie gorącą wodą)",
      no_tap_warning:
        "Do wykonania usługi wymagany jest dostępny kran z zimną wodą na zewnątrz. Zaznacz pole tylko jeśli go posiadasz.",
      back: "Wstecz",
      next: "Dalej",
      review_title: "Sprawdź i potwierdź",
      total: "Razem",
      confirm: "Potwierdź rezerwację",
      success_title: "Rezerwacja potwierdzona!",
      success_text: "Otrzymaliśmy Twoją rezerwację. Skontaktujemy się telefonicznie, aby potwierdzić szczegóły.",
      booking_id: "Numer rezerwacji",
      back_home: "Wróć na stronę główną",
    },
    admin: {
      login: "Logowanie administratora",
      email: "E-mail",
      password: "Hasło",
      sign_in: "Zaloguj",
      logout: "Wyloguj",
      dashboard: "Panel",
      bookings: "Rezerwacje",
      services_tab: "Usługi",
      customers: "Klienci",
      messages: "Wiadomości",
      messages_empty: "Brak rozmów.",
      messages_select: "Wybierz rozmowę",
      messages_select_hint:
        "Wskaż klienta po lewej, aby zobaczyć historię i odpowiedzieć.",
      messages_typing_placeholder: "Odpowiedz klientowi…",
      messages_send: "Wyślij",
      messages_open_booking: "Otwórz rezerwację",
      messages_unread: "nieprzeczyt.",
      messages_search_placeholder:
        "Szukaj po imieniu, numerze lub telefonie…",
      messages_visitor_badge: "Gość",
      messages_archived_badge: "Zarchiwizowane",
      messages_archive: "Archiwizuj rozmowę",
      messages_unarchive: "Cofnij archiwizację",
      messages_show_archived: "Pokaż zarchiwizowane",
      messages_delete: "Usuń rozmowę",
      messages_delete_confirm_title: "Usunąć tę rozmowę?",
      messages_delete_confirm_body:
        "Wszystkie wiadomości zostaną trwale usunięte. Tej operacji nie można cofnąć.",
      messages_delete_confirm: "Usuń trwale",
      messages_archived_toast: "Rozmowa zarchiwizowana",
      messages_unarchived_toast: "Rozmowa przywrócona",
      messages_deleted_toast: "Rozmowa usunięta",
      contact_title: "Publiczne dane kontaktowe",
      contact_subtitle:
        "Wyświetlane w stopce strony, w JSON-LD do SEO oraz w każdym e-mailu do klienta. Zmiany są widoczne natychmiast.",
      contact_email_label: "Publiczny adres e-mail",
      contact_phone_label: "Publiczny numer telefonu",
      contact_address_pl_label: "Adres (po polsku)",
      contact_address_en_label: "Adres (po angielsku)",
      contact_save: "Zapisz zmiany",
      contact_discard: "Anuluj",
      contact_last_saved: "Ostatnio zapisane",
      contact_saved: "Dane kontaktowe zaktualizowane",
      contact_save_failed: "Nie udało się zapisać — spróbuj ponownie.",
      contact_reviews_baseline_label: "Bazowa liczba opinii",
      contact_reviews_baseline_help:
        "Liczba pokazywana w sekcji hero. Wygenerowane lub dodane opinie są doliczane do tej wartości — ustaw 0, aby publiczny licznik pokazywał dokładnie liczbę realnych opinii.",
      reviews_title: "Opinie klientów",
      reviews_subtitle:
        "Wygeneruj jednym kliknięciem dwujęzyczne (PL + EN) opinie pisane przez AI lub usuń te, które Ci nie pasują. Pojawią się w karuzeli na stronie głównej i zasilą publiczny licznik opinii.",
      reviews_total_label: "Publiczny licznik pokazuje",
      reviews_generate_count_label: "Ile wygenerować",
      reviews_generate_btn: "Generuj",
      reviews_generate_help:
        "Używa Gemini Flash. Każda generacja tworzy unikalne opinie z realistycznymi polskimi imionami i dzielnicami Gdańska. Maks. 20 na kliknięcie.",
      reviews_generated_toast: "Dodano {n} nowych opinii",
      reviews_generate_failed: "Nie udało się wygenerować — spróbuj ponownie.",
      reviews_deleted_toast: "Opinia usunięta",
      reviews_delete_failed: "Nie udało się usunąć — spróbuj ponownie.",
      reviews_delete: "Usuń opinię",
      reviews_list_label: "Istniejące opinie",
      reviews_empty: "Brak opinii — kliknij Generuj, aby dodać kilka.",
      services_add: "Dodaj usługę",
      services_edit: "Edytuj",
      services_save: "Zapisz",
      services_cancel: "Anuluj",
      services_delete: "Usuń",
      services_active: "Aktywna",
      services_inactive: "Wyłączona",
      services_slug: "Slug (URL)",
      services_name_en: "Nazwa (EN)",
      services_name_pl: "Nazwa (PL)",
      services_unit_en: "Jednostka (EN)",
      services_unit_pl: "Jednostka (PL)",
      services_price: "Cena bazowa (zł)",
      services_duration: "Czas trwania (min)",
      services_desc_en: "Opis (EN)",
      services_desc_pl: "Opis (PL)",
      services_features_en: "Cechy (EN, jedna na linię)",
      services_features_pl: "Cechy (PL, jedna na linię)",
      services_saved: "Usługa zapisana",
      services_deleted: "Usługa usunięta",
      services_created: "Usługa utworzona",
      services_delete_confirm_title: "Usunąć tę usługę?",
      services_delete_confirm_body:
        "Zostanie trwale usunięta. Istniejące rezerwacje zachowują dane usługi.",
      change_password: "Zmień hasło",
      change_password_subtitle:
        "Zmień hasło logowania do panelu administratora.",
      current_password: "Aktualne hasło",
      new_password: "Nowe hasło",
      confirm_new_password: "Potwierdź nowe hasło",
      password_min_8: "Hasło musi mieć co najmniej 8 znaków",
      passwords_do_not_match: "Hasła nie są zgodne",
      password_changed: "Hasło zmienione",
      forgot_password_link: "Nie pamiętasz hasła?",
      forgot_password_title: "Zresetuj hasło",
      forgot_password_subtitle:
        "Podaj e-mail administratora — wyślemy link do ustawienia nowego hasła.",
      forgot_password_submit: "Wyślij link resetujący",
      forgot_password_sent:
        "Jeśli ten adres należy do konta administratora, link resetujący jest w drodze.",
      forgot_password_back: "Powrót do logowania",
      reset_password_title: "Ustaw nowe hasło",
      reset_password_subtitle:
        "Wybierz nowe hasło do panelu. Link jest ważny 60 minut.",
      reset_password_submit: "Zapisz nowe hasło",
      reset_password_success:
        "Hasło zostało zmienione — zaloguj się przy użyciu nowego hasła.",
      reset_password_invalid:
        "Link jest nieprawidłowy lub wygasł. Poproś o nowy, aby kontynuować.",
      settings: "Ustawienia",
      kpi_today: "Rezerwacje dziś",
      kpi_week: "Rezerwacje w tym tygodniu",
      kpi_revenue: "Przychód (tydzień)",
      kpi_completion: "Wskaźnik zakończonych",
      filter_status: "Status",
      filter_service: "Usługa",
      filter_from: "Od",
      filter_to: "Do",
      all: "Wszystkie",
      timeline: "Oś statusów",
      update_status: "Zmień status",
      blackouts: "Dni niedostępne",
      add_blackout: "Dodaj dzień niedostępny",
      reason: "Powód",
      add: "Dodaj",
      remove: "Usuń",
      archive: "Archiwizuj",
      unarchive: "Cofnij archiwizację",
      show_archived: "Pokaż zarchiwizowane",
      archived_label: "Zarchiwizowane",
      delete: "Do kosza",
      delete_confirm_title: "Przenieść tę rezerwację do kosza?",
      delete_confirm_body:
        "Rezerwacja zostanie przeniesiona do zakładki Kosz. Możesz ją później przywrócić lub trwale usunąć stamtąd.",
      delete_cancel: "Anuluj",
      delete_confirm: "Przenieś do kosza",
      deleted_toast: "Przeniesiono do kosza",
      trash: "Kosz",
      restore: "Przywróć",
      restored_toast: "Przywrócono",
      purge: "Usuń trwale",
      purge_confirm_title: "Trwale usunąć tę rezerwację?",
      purge_confirm_body:
        "Rezerwacja zostanie trwale usunięta. Tej operacji nie można cofnąć.",
      purge_confirm: "Usuń trwale",
      purged_toast: "Trwale usunięto",
      empty_trash: "Opróżnij kosz",
      empty_trash_confirm_title: "Opróżnić kosz?",
      empty_trash_confirm_body:
        "Wszystkie rezerwacje z kosza zostaną trwale usunięte. Tej operacji nie można cofnąć.",
      trash_empty: "Kosz jest pusty",
    },
    portal: {
      nav: "Moja rezerwacja",
      title: "Zarządzaj swoją rezerwacją",
      subtitle:
        "Wyszukaj rezerwację po numerze i e-mailu lub telefonie — edytuj szczegóły, przełóż termin albo napisz do nas na żywo.",
      lookup_reference: "Numer rezerwacji",
      lookup_reference_placeholder: "np. GDA-1234-A9F",
      lookup_contact: "E-mail lub telefon",
      lookup_contact_placeholder: "ty@example.com lub +48 …",
      lookup_btn: "Otwórz moją rezerwację",
      lookup_help:
        "Numer znajdziesz na ekranie potwierdzenia oraz w wiadomościach od nas.",
      lookup_error_not_found: "Nie znaleźliśmy rezerwacji o podanych danych.",
      lookup_error_mismatch:
        "E-mail lub telefon nie pasuje do tej rezerwacji.",
      lookup_error_generic: "Coś poszło nie tak. Spróbuj ponownie.",
      errors: {
        generic:
          "Coś nie zadziałało — spróbuj ponownie. Jeśli problem się powtórzy, napisz do nas — pomożemy.",
        offline:
          "Wygląda na to, że nie masz połączenia z internetem. Sprawdź łączność i spróbuj ponownie.",
        network:
          "Nie udało się połączyć z naszym serwerem. Sprawdź połączenie i spróbuj ponownie — Twoje dane nadal tu są.",
        timeout:
          "Żądanie trwało zbyt długo. Spróbuj ponownie za chwilę.",
        bad_request:
          "Niektóre dane wyglądają na nieprawidłowe. Sprawdź formularz i spróbuj ponownie.",
        session_expired:
          "Twoja sesja wygasła. Wyszukaj rezerwację ponownie.",
        forbidden:
          "Nie masz uprawnień do wykonania tej akcji w tej rezerwacji.",
        not_found:
          "Nie znaleźliśmy tej rezerwacji — mogła zostać anulowana lub usunięta.",
        lookup_not_found:
          "Żadna rezerwacja nie pasuje do tego numeru i kontaktu. Sprawdź oba pola i spróbuj ponownie.",
        lookup_mismatch:
          "Numer rezerwacji jest poprawny, ale e-mail lub telefon do niego nie pasują.",
        conflict:
          "Ta akcja koliduje z aktualnym stanem rezerwacji. Odśwież stronę i spróbuj ponownie.",
        slot_taken:
          "Ten termin został właśnie zarezerwowany. Wybierz inną godzinę.",
        already_cancelled:
          "Ta rezerwacja została już anulowana.",
        gone:
          "Ta rezerwacja nie jest już dostępna — mogła zostać zrealizowana lub zarchiwizowana.",
        validation:
          "Niektóre pola są wypełnione niepoprawnie. Sprawdź formularz i spróbuj ponownie.",
        rate_limited:
          "Zbyt wiele żądań w krótkim czasie. Poczekaj chwilę i spróbuj ponownie.",
        server:
          "Nasze serwery mają teraz mały problem. Spróbuj ponownie za minutę.",
      },
      details_tab: "Szczegóły rezerwacji",
      chat_tab: "Czat na żywo",
      reference: "Numer",
      status: "Status",
      scheduled: "Zaplanowana na",
      reschedule: "Zmień termin",
      reschedule_title: "Wybierz nowy termin",
      reschedule_save: "Zatwierdź nowy termin",
      cancel: "Anuluj rezerwację",
      cancel_confirm_title: "Anulować tę rezerwację?",
      cancel_confirm_body:
        "Oznaczymy rezerwację jako anulowaną. W każdej chwili możesz utworzyć nową ze strony głównej.",
      cancel_back: "Zostaw rezerwację",
      cancel_yes: "Tak, anuluj",
      cancelled_toast: "Rezerwacja anulowana",
      saved_toast: "Rezerwacja zaktualizowana",
      rescheduled_toast: "Termin zmieniony",
      readonly_notice:
        "Rezerwacja w statusie {status} — szczegóły są tylko do odczytu. Skontaktuj się przez czat.",
      service_label: "Usługa i ilość",
      contact_section: "Twoje dane",
      address_section: "Adres usługi",
      taps_section: "Dostęp do wody",
      save: "Zapisz zmiany",
      logout: "Wyloguj z portalu",
      empty_chat: "Brak wiadomości — przywitaj się!",
      chat_placeholder: "Napisz wiadomość…",
      chat_send: "Wyślij",
      chat_admin_label: "Zespół LocalH2O",
      chat_you: "Ty",
      chat_offline_warning: "Brak połączenia — łączymy ponownie…",
      chat_intro:
        "Pisz bezpośrednio z zespołem LocalH2O. Odpowiedzi przychodzą na żywo.",
    },
    errors: {
      required: "To pole jest wymagane",
      email_invalid: "Podaj poprawny adres e-mail",
      phone_short: "Podaj poprawny numer telefonu",
      cold_tap_required: "Wymagany jest dostępny zewnętrzny kran z zimną wodą",
      slot_taken: "Niestety, ten termin został właśnie zajęty — wybierz inny.",
      date_unavailable: "Ten dzień jest niedostępny. Wybierz inną datę.",
      generic: "Nie udało się utworzyć rezerwacji. Sprawdź wprowadzone dane.",
    },
    status: {
      pending: "Oczekujące",
      confirmed: "Potwierdzone",
      in_progress: "W realizacji",
      completed: "Zakończone",
      cancelled: "Anulowane",
    },
    common: {
      currency: "zł",
      lang: "PL",
      switch_lang: "Zmień język",
    },
    seo: {
      home_title:
        "LocalH2O — Mycie ciśnieniowe i czyszczenie kubłów · Gdańsk",
      home_desc:
        "Ekologiczne mycie ciśnieniowe podjazdów oraz higieniczne czyszczenie kubłów na śmieci w Gdańsku i całym Pomorskiem. Rezerwacja online w 60 sekund, płatność po wykonaniu.",
      service_driveway_title:
        "Mycie ciśnieniowe w Gdańsku — podjazdy, tarasy, ścieżki · LocalH2O",
      service_driveway_desc:
        "Przywróć blask podjazdowi, tarasowi i ścieżkom z LocalH2O. Od 10 zł/m². Rezerwuj online — Gdańsk, Sopot, Gdynia i całe Pomorskie.",
      service_bin_title:
        "Mycie i dezynfekcja kubłów na śmieci · Gdańsk · LocalH2O",
      service_bin_desc:
        "Higieniczne mycie kubłów na miejscu z bezpiecznym środkiem dezynfekującym — 35 zł za kubeł. Gdańsk, Sopot, Gdynia i całe Pomorskie.",
      booking_title: "Zarezerwuj usługę · LocalH2O",
      booking_desc:
        "Zarezerwuj termin mycia ciśnieniowego lub czyszczenia kubłów w 60 sekund. Płatność po wykonaniu. LocalH2O · Gdańsk.",
      confirm_title: "Rezerwacja potwierdzona · LocalH2O",
      confirm_desc: "Twoja rezerwacja LocalH2O została przyjęta.",
      admin_title: "Panel · LocalH2O",
      admin_desc: "Panel administracyjny LocalH2O.",
      portal_title: "Twoja rezerwacja · LocalH2O",
      portal_desc:
        "Wyświetl, edytuj, przełóż termin lub anuluj rezerwację LocalH2O — i pisz z nami na żywo w portalu klienta.",
    },
  },
};

const I18nContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (path) => path,
});

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(
    localStorage.getItem("lh2o_lang") || "pl",
  );
  useEffect(() => {
    localStorage.setItem("lh2o_lang", lang);
    document.documentElement.lang = lang;
    const map = {
      "favicon-ico": `/favicon-${lang}.ico`,
      "favicon-32": `/favicon-${lang}-32.png`,
      "favicon-16": `/favicon-${lang}-16.png`,
      "favicon-apple": `/apple-touch-icon-${lang}.png`,
      "favicon-192": `/favicon-${lang}-192.png`,
    };
    Object.entries(map).forEach(([id, href]) => {
      const el = document.getElementById(id);
      if (el) el.setAttribute("href", href);
    });
  }, [lang]);
  const setLang = (l) => setLangState(l);
  const t = (path) => {
    const parts = path.split(".");
    let node = dict[lang];
    for (const p of parts) {
      if (node == null) return path;
      node = node[p];
    }
    return node == null ? path : node;
  };
  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
